import { ErrorMessage } from '@/components/loading-error';
import { EnhancedVAD, WavRecorder, WavStreamPlayer } from '@/lib/wavtools';
import { ELEVEN_LABS_AGENT_ID, SUPPORTED_LANGUAGES } from '@/utils/constants';
import { Conversation } from '@elevenlabs/client';
import { AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ControlPanel } from '../../components/ui/controlpanel';
import { Header } from '../../components/ui/header';
import { LoadingOverlay } from '../../components/ui/loading';
import {
  RolePlayArea,
  ScenarioInfo,
  SessionPhase,
} from '../../components/ui/scenarioinfo';

import { useCoachStore } from '@/store/coach';
import { usePostHog } from 'posthog-js/react';
import ConnectionMonitor from '../../components/connection-monitor';
import { constructLanguageUrl } from '../../router';
import api from '../../utils/ky';

// Helper function to convert base64 string to Blob
function base64ToBlob(base64: string, mime: string) {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mime });
}

export function CoachRolePlay11() {
  const navigate = useNavigate();
  const coachStore = useCoachStore();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const code = searchParams.get('code');
  const { lang } = useParams();

  // State
  const [loadingState, setLoadingState] = useState('initial');
  const [sessionPhase, setSessionPhase] = useState<SessionPhase>(
    SessionPhase.PRE_START,
  );
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isIntroCompleted, setIsIntroCompleted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const startTimeRef = useRef<number>(0);
  const rolePlaySessionRef = useRef<any>(null);
  const isIntroCompletedRef = useRef(false);
  const introResponseIdRef = useRef<string | null>(null);
  const isIntroMessageCompleteRef = useRef(false);

  const conversationRef = useRef<Conversation | null>(null);
  const practiceStartedRef = useRef(false);

  // Audio tools
  const wavRecorderRef = useRef<any>(new WavRecorder({ sampleRate: 24000 }));
  const wavStreamPlayerRef = useRef<any>(
    new WavStreamPlayer({ sampleRate: 24000 }),
  );
  const vadRef = useRef(new EnhancedVAD());

  // Transcript state
  const [transcript, setTranscript] = useState<
    Array<{
      source: 'ai' | 'user';
      message: string;
      timestamp: Date;
    }>
  >([]);

  const ttsSocketRef = useRef<WebSocket | null>(null);
  const ttsAudioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize TTS WebSocket on mount
  useEffect(() => {
    const ttsSocket = new WebSocket(import.meta.env.VITE_TTS_WEBSOCKET_URI);
    ttsSocketRef.current = ttsSocket;

    ttsSocket.onopen = () => {
      console.log('TTS WebSocket connected for intro');
    };

    ttsSocket.onmessage = (message) => {
      // console.log("Received TTS audio:", message.data);
      const data = JSON.parse(message.data);
      if (data.audioContent) {
        const audioBlob = base64ToBlob(data.audioContent, 'audio/mp3');
        const url = URL.createObjectURL(audioBlob);
        const audio = new Audio(url);
        ttsAudioRef.current = audio;
        audio.play();
        audio.onended = () => {
          console.log('Intro audio playback finished.');
          // Mark intro as completed and connect to main conversation using ElevenLabs
          isIntroCompletedRef.current = true;
          setIsIntroCompleted(true);
          connectConversation();
        };
      }
    };

    ttsSocket.onerror = (error) => {
      console.error('TTS WebSocket error:', error);
      console.log(error);
    };

    return () => {
      ttsSocket.close();
    };
  }, []);

  const fetchSession = useCallback(async () => {
    console.log('Fetching session:', sessionId);
    try {
      setLoadingState('loading');
      const data: any = await api
        .get(
          `api/coach/sessions/${sessionId}${lang ? `?language=${lang}` : ''}`,
        )
        .json();
      console.log('Fetched session:', data);

      rolePlaySessionRef.current = data;
      setLoadingState('ready');

      posthog.capture('coach_roleplay_loaded', {
        code: searchParams.get('code'),
        userId: coachStore.userId,
        sessionId: data.sessionId,
      });
    } catch (error) {
      console.error('Error fetching session:', error);
      setLoadingState('error');
      toast.error('Failed to fetch session. Please try again.');
    }
  }, [sessionId, lang, searchParams, coachStore.userId, posthog]);

  useEffect(() => {
    console.log('useEffect Scenario:', sessionId);
    if (sessionId) {
      fetchSession();
    }
  }, [sessionId, fetchSession]);

  // Modified connectConversation: it uses ElevenLabs for main conversation when intro is complete
  const connectConversation = async () => {
    console.log('Connecting conversation');
    try {
      setError(null);
      practiceStartedRef.current = true;

      const session = rolePlaySessionRef.current;
      if (!session) {
        throw new Error('No session data found');
      }

      // Configure prompt based on intro/main conversation
      // const isIntro = !isIntroCompletedRef.current;
      const prompt =
        session.voiceInstructions?.chat +
        `
      user full name: ${session.user.name}
      this is the main role-play conversation
      ${
        session.aiConfig?.initialPrompt
          ? `\nIMPORTANT: ${session.aiConfig.initialPrompt}`
          : ''
      }
      `;
      // console.log('Prompt:', prompt);

      const conversation = await Conversation.startSession({
        agentId: ELEVEN_LABS_AGENT_ID,
        connectionType: 'webrtc',
        overrides: {
          agent: {
            prompt: { prompt },
            firstMessage:
              SUPPORTED_LANGUAGES.find((l) => l.code === lang)?.firstMessage ||
              'Hello',
            language: (lang || 'en') as any,
          },
          ...SUPPORTED_LANGUAGES.reduce((acc, language) => {
            if (lang === language.code) {
              return { ...acc, tts: { voiceId: language.voiceId } };
            }
            return acc;
          }, {}),
        },
        onConnect: () => {
          startTimeRef.current = Date.now();
          setSessionPhase(SessionPhase.MAIN_CONVERSATION);
        },
        onDisconnect: () => {
          setIsAISpeaking(false);
        },
        onError: (error) => {
          setError(error || 'Connection Error');
        },
        onModeChange: ({ mode }) => {
          setIsAISpeaking(mode === 'speaking');
        },
        onMessage({ source, message }) {
          console.log('New message received:', {
            source: source,
            content: message,
            time: new Date().toISOString(),
          });

          setTranscript((prev) => [
            ...prev,
            {
              source,
              message,
              timestamp: new Date(),
            },
          ]);

          if (source === 'ai') {
            console.log('AI response:', message);
          } else {
            console.log('User message:', message);
          }
        },
      });

      conversationRef.current = conversation;
    } catch (err: any) {
      setError(err.message || 'Failed to connect to AI Coach');
    }
  };

  const checkMediaDevices = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Your browser does not support audio input');
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasAudioInput = devices.some(
        (device) => device.kind === 'audioinput',
      );
      if (!hasAudioInput) {
        alert('No audio input devices detected');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      if ((error as any).name === 'NotAllowedError') {
        alert(
          'Microphone permission denied. Please allow microphone access to use this feature',
        );
      } else if ((error as any).name === 'NotFoundError') {
        alert('No microphone found. Please connect a microphone and try again');
      }
      throw error;
    }
  };

  // Modified handleStartPractice: now sends intro text to TTS socket instead of connecting immediately
  const handleStartPractice = async () => {
    try {
      await checkMediaDevices();
      const session = rolePlaySessionRef.current;
      if (session && session.voiceInstructions?.intro) {
        if (
          ttsSocketRef.current &&
          ttsSocketRef.current.readyState === WebSocket.OPEN
        ) {
          const introText =
            "Hi in this role-play, I'll be acting as Emily. To get started, you can say something like 'Hey Emily, how have you been doing?' and I will respond as Emily in this roleplay.";
          const introTextFr =
            "Salut, dans ce jeu de rôle, je vais jouer le rôle d'Emily. Pour commencer, vous pouvez dire quelque chose comme 'Salut Emily, comment ça va?' et je répondrai en tant qu'Emily dans ce jeu de rôle.";
          const introTextJa =
            'こんにちは、このロールプレイでは、私がエミリー役を演じます。始めるには、「こんにちは、エミリー、元気ですか？」のようなことを言ってください。このロールプレイでは、エミリーとして返信します。';
          const introTextZh =
            '你好，在这个角色扮演中，我将扮演埃米莉。要开始，你可以用“你好，埃米莉，你做得怎么样？”之类的话，我将作为埃米莉在角色扮演中回应。';
          const introTextKo =
            '안녕하세요，在这个角色扮演中，我将扮演埃米莉。要开始，你可以用“안녕하세요，에밀리，어떻게 지내셨나요？”之类的话，我将作为埃米莉在角色扮演中回应。';

          ttsSocketRef.current.send(
            JSON.stringify({
              text:
                lang === 'fr'
                  ? introTextFr
                  : lang === 'ja'
                  ? introTextJa
                  : lang === 'zh'
                  ? introTextZh
                  : lang === 'ko'
                  ? introTextKo
                  : introText,
              languageCode: SUPPORTED_LANGUAGES.find(l => l.code === lang)?.TTS || 'en-US',
            }),
          );
        } else {
          console.error(
            'TTS socket is not open, falling back to ElevenLabs connection.',
          );
          isIntroCompletedRef.current = true;
          setIsIntroCompleted(true);
          connectConversation();
        }
        setSessionPhase(SessionPhase.INTRO);
      } else {
        isIntroCompletedRef.current = true;
        setIsIntroCompleted(true);
        connectConversation();
      }
      posthog.capture('coach_roleplay_started', {
        code: searchParams.get('code'),
        userId: coachStore.userId,
        sessionId: rolePlaySessionRef.current?.sessionId,
        scenario: rolePlaySessionRef.current?.scenario.title,
      });
    } catch (error) {
      console.error('Error starting practice:', error);
      toast.error(
        'Failed to start practice. Please check your microphone permissions.',
      );
    }
  };

  // Modified handleSkipIntro: if user skips, mark intro as complete and connect to main conversation (ElevenLabs)
  const handleSkipIntro = useCallback(async () => {
    console.log('manually skipping intro');
    if (ttsAudioRef.current) {
      ttsAudioRef.current.pause();
      ttsAudioRef.current.currentTime = 0;
      ttsAudioRef.current = null;
    }
    setSessionPhase(SessionPhase.MAIN_CONVERSATION);
    try {
      isIntroCompletedRef.current = true;
      await navigator.mediaDevices.getUserMedia({ audio: true });
      await connectConversation();
    } catch (err) {
      console.error('Error during intro skip:', err);
      await connectConversation();
    }
  }, [connectConversation]);

  const handleEndPractice = useCallback(async () => {
    console.log('handleEndPractice');
    console.log('Resultant transcript is: ', transcript);
    const formattedTranscript = transcript
      .map(
        (msg) =>
          `${msg.source === 'ai' ? 'Assistant' : 'User'}: ${msg.message}`,
      )
      .join('\n');

    cleanupResources();
    console.log('formattedTranscript  :', formattedTranscript);
    await endSession({
      transcript: formattedTranscript,
      duration: (Date.now() - startTimeRef.current) / 1000,
    });
  }, [transcript]);

  const cleanupResources = useCallback(() => {
    const wavStreamPlayer = wavStreamPlayerRef.current;
    if (wavStreamPlayer) {
      wavStreamPlayer.interrupt();
    }

    const wavRecorder = wavRecorderRef.current;
    if (wavRecorder && wavRecorder.getStatus() !== 'ended') {
      wavRecorder.end();
      wavRecorder.quit();
    }

    vadRef.current.reset();
    setIsAISpeaking(false);
    conversationRef.current?.endSession();
  }, []);

  const endSession = async ({
    transcript,
    duration,
  }: {
    transcript: string;
    duration: number;
  }) => {
    try {
      setLoadingState('ending');

      posthog.capture('coach_roleplay_ended', {
        code: searchParams.get('code'),
        userId: coachStore.userId,
        sessionId: rolePlaySessionRef.current?.sessionId,
        scenario: rolePlaySessionRef.current?.scenario.title,
      });

      await api
        .post('api/coach/roleplay/end-session', {
          timeout: 90000,
          retry: {
            limit: 2,
            methods: ['post'],
          },
          json: {
            sessionId,
            transcript,
            duration,
            language: lang // Add language parameter for localization
          },
        })
        .json();

      coachStore.setLastSessionId(sessionId);
      coachStore.setSessionTranscript(transcript);
      setLoadingState(SessionPhase.DONE);

      navigate(
        constructLanguageUrl(
          lang,
          '/coach/roleplay-feedback',
          '?' +
            new URLSearchParams({
              code,
              sessionId,
            }).toString(),
        ),
      );
    } catch (error) {
      console.error('Error ending session:', error);
      toast.error('Failed to end session. Please try again.');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {error && <ErrorMessage message={error} onRetry={connectConversation} />}
      <Header
        scenario={rolePlaySessionRef.current?.scenario}
        startTime={startTimeRef.current}
      />
      <div className="flex-1 flex overflow-hidden">
        <div className="w-1/3 bg-white border-r border-gray-200 overflow-y-auto">
          <ScenarioInfo
            scenario={rolePlaySessionRef.current?.scenario}
            framework={rolePlaySessionRef.current?.framework}
            phase={sessionPhase}
            onStart={handleStartPractice}
            className="px-6 py-4"
          />
        </div>
        <div className="flex-1 flex flex-col relative">
          <RolePlayArea
            isAISpeaking={isAISpeaking}
            isMuted={false}
            scenario={rolePlaySessionRef.current?.scenario}
            volume={100}
          />
          <ConnectionMonitor className="flex items-center justify-center p-2 text-sm mb-24" />
          <ControlPanel
            phase={sessionPhase}
            isAISpeaking={isAISpeaking}
            onEnd={handleEndPractice}
            onSkipIntro={handleSkipIntro}
            isIntroCompleted={isIntroCompleted}
          />
        </div>
      </div>
      <AnimatePresence>
        {loadingState !== 'ready' && <LoadingOverlay state={loadingState} />}
      </AnimatePresence>
    </div>
  );
}
